// ════════════════════════════════════════════════════════════════════════════
// Minimum de facturation de la PRESTATION DE TRITURATION — règle unique.
//
// Pour les prestations CLIENT uniquement : la trituration est facturée au minimum
// pour `seuil` kg au tarif applicable au lot.
//   montant normal = poids net réel du passage × tarif
//   minimum        = seuil × tarif
//   complément     = max(0, minimum − montant normal)
// Exemple : 300 kg à 0,200 → 60,000 + complément 40,000 = 100,000 TND (= 500 × 0,200).
//
// Le complément est calculé UNE SEULE FOIS par passage réellement trituré ensemble,
// puis réparti entre ses bons au prorata du poids net (arrondi ajusté sur le dernier
// bon : la somme des parts est exactement égale au complément).
//
// Le poids physique n'est JAMAIS modifié : poids de pesée, tonnages, rendements,
// huile attribuée, cuves, CMP et droits clients restent sur le poids réel.
// ════════════════════════════════════════════════════════════════════════════

export const SEUIL_DEFAUT = 500;

export const a3 = (valeur) => Number((Number(valeur) || 0).toFixed(3));

// Paramètres généraux lus dans la grille tarifaire existante.
export const parametresMinimum = (tarifs) => ({
  actif: tarifs?.minimumActif !== false,
  seuil: Number(tarifs?.minimumSeuilKg) > 0 ? Number(tarifs.minimumSeuilKg) : SEUIL_DEFAUT,
});

export function complementPassage({ seuil, tarif, baseKg }) {
  const t = Number(tarif) || 0;
  const s = Number(seuil) || 0;
  const b = Number(baseKg) || 0;
  if (!(t > 0) || !(s > 0)) return 0;
  return a3(Math.max(0, a3(s * t) - a3(b * t)));
}

export function repartirComplement(complement, bons) {
  const liste = bons ?? [];
  const total = liste.reduce((s, b) => s + (Number(b.poidsNet) || 0), 0);
  if (!(complement > 0) || !(total > 0) || !liste.length) return liste.map((b) => ({ id: b.id, part: 0 }));
  const parts = liste.map((b) => ({ id: b.id, part: a3((complement * (Number(b.poidsNet) || 0)) / total) }));
  const cumul = a3(parts.slice(0, -1).reduce((s, p) => s + p.part, 0));
  parts[parts.length - 1] = { ...parts[parts.length - 1], part: a3(complement - cumul) };
  return parts;
}

// Un passage ne se calcule jamais en silence si ses bons ne portent pas le même
// tarif ou la même décision de dérogation : harmonisation par un utilisateur autorisé.
export function passageHomogene(bons) {
  const liste = bons ?? [];
  const tarifs = [...new Set(liste.map((b) => a3(b.tarifTrituration)))];
  const decisions = [...new Set(liste.map((b) => (b.minTrit?.derogation ? 'derogation' : 'applique')))];
  return { tarifs, decisions, homogene: tarifs.length <= 1 && decisions.length <= 1 };
}

// Instantané CONSERVÉ AVEC L'OPÉRATION : seuil, tarif, base réelle, complément,
// passage et décision. Les changements ultérieurs de paramètres ne le réécrivent pas.
export function instantaneMinimum({
  bon,
  bonsPassage,
  params,
  exempt = false,
  passageId = null,
  derogation = null,
  estimation = true,
  fige = false,
}) {
  const liste = bonsPassage?.length ? bonsPassage : [bon];
  const tarif = a3(bon.tarifTrituration);
  const seuil = Number(params?.seuil) > 0 ? Number(params.seuil) : SEUIL_DEFAUT;
  const baseKg = a3(liste.reduce((s, b) => s + (Number(b.poidsNet) || 0), 0));
  const actif = params?.actif === true && exempt !== true && !derogation;
  const total = complementPassage({ seuil, tarif, baseKg });
  const part = actif ? repartirComplement(total, liste).find((p) => p.id === bon.id)?.part ?? 0 : 0;
  return {
    v: 1,
    actif,
    exempt: exempt === true,
    seuil,
    tarif,
    passageId: passageId ?? null,
    bons: liste.map((b) => b.id),
    baseKg,
    partKg: a3(bon.poidsNet),
    complementPassage: total,
    complement: part,
    estimation: estimation === true,
    fige: fige === true,
    derogation: derogation ?? null,
  };
}

// Mention explicative des documents (jamais d'identité d'autorisation côté client).
export const mentionMinimum = (inst) => {
  if (!inst) return null;
  if (inst.derogation) return 'Minimum de facturation non appliqué — dérogation commerciale.';
  if (!(Number(inst.complement) > 0)) return null;
  return `Minimum de facturation de la prestation : ${a3(inst.seuil)} kg. Le poids réellement reçu et les droits en huile restent inchangés.`;
};
